import React, { FC, Fragment } from 'react';
import { Trans } from 'react-i18next';
import { Button, Grid, makeStyles, Paper, Typography } from '@material-ui/core';

import { FeedKey, PublicUserProfileFragment } from 'api';
import { FollowToggle, IFollowToggleRenderProps, UserAvatar } from 'components';
import { getFullName } from 'helpers';

import Tabs from './Tabs';

interface IProps {
  profile: PublicUserProfileFragment;
}

const useStyles = makeStyles(theme => ({
  container: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    borderBottom: `1px solid ${theme.custom.palette.grey3}`,
  },
  meta: {
    fontWeight: theme.typography.fontWeightRegular,
  },
  lastItem: {
    marginRight: theme.spacing(2),
  },
}));

const Header: FC<IProps> = React.memo(function HeaderMemo(props) {
  const classes = useStyles();
  const { profile } = props;
  const { job_title, department_title, location_title } = profile;

  function renderFollowButton(onClick: IFollowToggleRenderProps['onClick']) {
    return (
      <Button size="large" color="primary" onClick={onClick} variant="outlined">
        <Trans i18nKey="common:follow" />
      </Button>
    );
  }

  function renderFollowingButton(onClick: IFollowToggleRenderProps['onClick']) {
    return (
      <Button size="large" onClick={onClick} variant="outlined">
        <Trans i18nKey="common:following" />
      </Button>
    );
  }

  function renderDepartment() {
    return department_title ? <Fragment>- {department_title}</Fragment> : null;
  }

  function renderLocation() {
    return location_title ? <Fragment>- {location_title}</Fragment> : null;
  }

  function renderToggle() {
    return (
      <FollowToggle source={profile} type={FeedKey.User}>
        {({ disableToggle, isFollowing, onClick }) => {
          if (disableToggle) {
            return null;
          }

          return isFollowing ? renderFollowingButton(onClick) : renderFollowButton(onClick);
        }}
      </FollowToggle>
    );
  }

  function renderTopRow() {
    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <UserAvatar link user={profile} size={70} />
        </Grid>
        <Grid item xs>
          <Typography title={getFullName(profile)} color="textPrimary" variant="h4" noWrap>
            {getFullName(profile)}
          </Typography>
          <Typography
            title={job_title}
            className={classes.meta}
            color="textSecondary"
            variant="subtitle2"
            noWrap
          >
            {job_title} {renderDepartment()} {renderLocation()}
          </Typography>
        </Grid>
        <Grid item className={classes.lastItem}>
          {renderToggle()}
        </Grid>
      </Grid>
    );
  }

  return (
    <Paper>
      <Grid className={classes.container} container alignItems="center">
        <Grid item xs>
          {renderTopRow()}
        </Grid>
      </Grid>
      <Grid container>
        <Grid item>
          <Tabs profile={profile} />
        </Grid>
      </Grid>
    </Paper>
  );
});

export default Header;
